// Dana's external agent tools (Agent Platform 9F): tenant-configured MCP
// servers Dana may call — cross-system lookups freely, actions confirm-first.
// Tokens are encrypted at rest and shown masked; URLs must be public https
// (the server refuses private hosts). Native integrations (Developer →
// Integrations) still own synced, package-mapped state — this is for ad-hoc
// asks in chat.
import React, { useEffect, useState } from 'react';
import { getApiErrorMessage } from '@/lib/apiError';
import { useTranslation } from 'react-i18next';
import { Loader2, Plug2, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { Badge } from '@/features/shared/components/ui/badge';
import { Button } from '@/features/shared/components/ui/button';
import {
  getDanaMcpServers, setDanaMcpServers, type DanaMcpServer,
} from '@/services/developerService';
import { ActionRefusal } from '@/features/shared/components/ui/ActionRefusal';
import { errMsg, inputCls } from './common';

export const DanaMcpServersCard: React.FC = () => {
  const { t } = useTranslation('admin');
  const [servers, setServers] = useState<DanaMcpServer[]>([]);
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [token, setToken] = useState('');
  const [busy, setBusy] = useState(false);
  // The server's refusal (e.g. "This MCP server can't be reached from KaziPeople —
  // its URL must point at a public internet address."), kept under the form until
  // the user edits or retries. The typed values stay so they can be corrected.
  const [refusal, setRefusal] = useState<string | null>(null);

  const load = () => {
    getDanaMcpServers().then(setServers).catch(() => setServers([]));
  };
  useEffect(load, []);

  /** Resolves true when saved; on a refusal the reason stays on the card. */
  const save = async (next: Array<{ name: string; url: string; token?: string | null; enabled?: boolean }>): Promise<boolean> => {
    setBusy(true);
    setRefusal(null);
    try {
      setServers(await setDanaMcpServers(next));
      toast.success(t('developer.dana_mcp_saved', { defaultValue: 'Dana’s external agent tools updated.' }));
      return true;
    } catch (e: any) {
      setRefusal(getApiErrorMessage(e, errMsg(e, t('developer.dana_mcp_failed', { defaultValue: 'Could not save the servers.' }))));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const add = () => {
    if (!name.trim() || !url.trim()) return;
    const next = [
      ...servers.map((s) => ({ name: s.name, url: s.url, enabled: s.enabled })),
      { name: name.trim(), url: url.trim(), token: token.trim() || undefined, enabled: true },
    ];
    // Clear the inputs ONLY when the server accepted them: a refused URL used to
    // be wiped along with its explanation, so the card looked like nothing happened.
    save(next).then((ok) => { if (ok) { setName(''); setUrl(''); setToken(''); } });
  };

  const remove = (n: string) => {
    save(servers.filter((s) => s.name !== n).map((s) => ({ name: s.name, url: s.url, enabled: s.enabled })));
  };

  const toggle = (n: string) => {
    save(servers.map((s) => ({ name: s.name, url: s.url, enabled: s.name === n ? !s.enabled : s.enabled })));
  };

  return (
    <div className="rounded-lg border border-border bg-card p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Plug2 className="w-4 h-4 text-muted-foreground" />
        <h3 className="text-sm font-semibold text-foreground">
          {t('developer.dana_mcp_title', { defaultValue: 'Dana’s external agent tools (beta)' })}
        </h3>
      </div>
      <p className="text-sm text-muted-foreground">
        {t('developer.dana_mcp_desc', {
          defaultValue:
            'Connect MCP servers (Asana, Slack, your own systems) that Dana may use in chat: read-only lookups run freely; anything that changes the other system is confirmed with the user first. For recurring, synced workflows use the native integrations — this is for ad-hoc asks.',
        })}
      </p>
      {servers.length > 0 && (
        <div className="space-y-1.5">
          {servers.map((s) => (
            <div key={s.name} className="flex flex-wrap items-center gap-2 text-sm">
              <code className="text-foreground font-mono">{s.name}</code>
              <span className="text-xs text-muted-foreground truncate max-w-[320px]">{s.url}</span>
              {s.hasToken && (
                <Badge className="bg-muted text-muted-foreground border-border">
                  {t('developer.dana_mcp_token', { defaultValue: 'token …{{last4}}', last4: s.tokenLast4 })}
                </Badge>
              )}
              <Badge className={s.enabled
                ? 'bg-success-soft text-success-strong border-transparent'
                : 'bg-muted text-muted-foreground border-border'}>
                {s.enabled ? t('developer.enabled', { defaultValue: 'enabled' }) : t('developer.disabled', { defaultValue: 'disabled' })}
              </Badge>
              <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => toggle(s.name)} disabled={busy}>
                {s.enabled ? t('developer.disable', { defaultValue: 'Disable' }) : t('developer.enable', { defaultValue: 'Enable' })}
              </Button>
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-danger" onClick={() => remove(s.name)} disabled={busy}
                aria-label={t('developer.remove', { defaultValue: 'Remove' })}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}
      <div className="grid gap-2 sm:grid-cols-3">
        <input className={inputCls} placeholder={t('developer.dana_mcp_name_ph', { defaultValue: 'name (e.g. asana)' }) as string}
          value={name} onChange={(e) => { setName(e.target.value); setRefusal(null); }} />
        <input className={inputCls} placeholder="https://mcp.example.com/mcp"
          value={url} onChange={(e) => { setUrl(e.target.value); setRefusal(null); }} />
        <input className={inputCls} type="password"
          placeholder={t('developer.dana_mcp_token_ph', { defaultValue: 'bearer token (optional)' }) as string}
          value={token} onChange={(e) => setToken(e.target.value)} />
      </div>
      <ActionRefusal message={refusal} onDismiss={() => setRefusal(null)} testId="dana-mcp-refusal" />
      <Button size="sm" onClick={add} disabled={busy || !name.trim() || !url.trim()}>
        {busy && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
        {t('developer.dana_mcp_add', { defaultValue: 'Add server' })}
      </Button>
    </div>
  );
};
